import { useState, useEffect } from "react";
import heartLoading from "../../../assests/heartLoading.gif";

const CATEGORIES = ["Technical Metadata", "Business Description", "Transformation Logic"];

// Maps category label 
const FIELD_MAP = {
  "Technical Metadata":   "technicalMetadata",
  "Business Description": "businessDescription",
  "Transformation Logic": "transformationLogic",
};

// Maps API category strings (may vary in casing/wording) → our canonical labels
const normalizeCategory = (cat = "") => {
  const lower = cat.toLowerCase();
  if (lower.includes("technical"))      return "Technical Metadata";
  if (lower.includes("business"))       return "Business Description";
  if (lower.includes("transformation")) return "Transformation Logic";
  return cat; 
};

const SPIN = `@keyframes spin { to { transform: rotate(360deg); } }`;

const TYPE_COLORS = {
  string:  { bg: "#f3f0ff", color: "#7c3aed", border: "#ddd6fe" },
  double:  { bg: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" },
  integer: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
  boolean: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const StarIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ display: "inline", verticalAlign: "middle" }}>
    <path d="M12 2L14.09 8.26L21 9.27L16 14.14L17.18 21.02L12 17.77L6.82 21.02L8 14.14L3 9.27L9.91 8.26L12 2Z" />
  </svg>
);
const CheckIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevLeft = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ── Sub-components ─────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] || { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontSize: "11px", fontWeight: 500, padding: "1px 7px",
      borderRadius: "4px", display: "inline-block", whiteSpace: "nowrap",
    }}>
      {type}
    </span>
  );
}

function SuggestionCell({ suggested, status, onEdit }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{
        background:   status === "accepted" ? "#f0fdfa" : status === "rejected" ? "#fff5f5" : "#fff",
        border:       `1px solid ${status === "accepted" ? "#99f6e4" : status === "rejected" ? "#fca5a5" : "#e5e7eb"}`,
        borderRadius: "8px", padding: "8px 36px 8px 12px",
        fontSize: "13px", color: "#1f2937", minHeight: "36px", transition: "all 0.15s",
      }}>
        {suggested}
      </div>
      {hovered && (
        <button
          onClick={onEdit}
          style={{
            position: "absolute", top: "7px", right: "7px",
            padding: "3px 5px", background: "#fff", border: "1px solid #e5e7eb",
            borderRadius: "5px", cursor: "pointer", color: "#6b7280",
            display: "flex", alignItems: "center",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0d9488"; e.currentTarget.style.color = "#0d9488"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#6b7280"; }}
        >
          <EditIcon />
        </button>
      )}
    </div>
  );
}

function EditableCell({ value, onChange, onSave, onCancel }) {
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
        style={{
          width: "100%", border: "2px solid #0d9488", borderRadius: "8px",
          padding: "8px 12px", fontSize: "13px", color: "#111827",
          resize: "vertical", minHeight: "68px", outline: "none",
          fontFamily: "inherit", boxSizing: "border-box",
        }}
      />
      <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
        <button onClick={onSave}   style={{ padding: "5px 14px", background: "#0d9488", color: "#fff", border: "none", borderRadius: "6px", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>Save</button>
        <button onClick={onCancel} style={{ padding: "5px 14px", background: "#f3f4f6", color: "#6b7280", border: "none", borderRadius: "6px", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>Cancel</button>
      </div>
    </div>
  );
}

function ActionButtons({ status, onAccept, onReject, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
      <button
        onClick={onReject} disabled={disabled}
        style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 14px", borderRadius: "7px", fontSize: "12px", fontWeight: 500, cursor: disabled ? "default" : "pointer", background: "#fff", color: "#ef4444", border: "1px solid #fca5a5", opacity: disabled ? 0.4 : 1 }}
        onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "#fff5f5"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
      >
        <XIcon size={10} /> Reject
      </button>
      <button
        onClick={onAccept} disabled={disabled}
        style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 14px", borderRadius: "7px", fontSize: "12px", fontWeight: 500, cursor: disabled ? "default" : "pointer", background: "#0d9488", color: "#fff", border: "1px solid #0d9488", opacity: disabled ? 0.4 : 1 }}
        onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "#0f766e"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#0d9488"; }}
      >
        <CheckIcon size={10} /> Accept
      </button>
      {status && (
        <span style={{ display: "flex", alignItems: "center", gap: "4px", padding: "6px 12px", borderRadius: "7px", fontSize: "12px", fontWeight: 600, background: status === "accepted" ? "#0d9488" : "#ef4444", color: "#fff", whiteSpace: "nowrap" }}>
          {status === "accepted" ? <><CheckIcon size={10} /> Accepted</> : <><XIcon size={10} /> Rejected</>}
        </span>
      )}
    </div>
  );
}


export default function AIEditPage({ row, assetId, onBack, onSaveRow }) {
  const [apiRows,     setApiRows]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [statuses,    setStatuses]    = useState({});   
  const [editingCat,  setEditingCat]  = useState(null);
  const [editValue,   setEditValue]   = useState("");
  const [hasError,    setHasError]    = useState(false);

  // ── Fetch suggestions ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setStatuses({});
      setEditingCat(null);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/metadata/compare?asset_id=${assetId}&column_name=${row.fieldName}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json(); 

        if (!cancelled) {
          const normalized = json
            .map((item) => ({ ...item, category: normalizeCategory(item.category) }))
            .filter((item) => CATEGORIES.includes(item.category));
          setApiRows(normalized);
        }
      } catch {
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [assetId, row.fieldName]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const setStatus  = (cat, s) => setStatuses((p) => ({ ...p, [cat]: s }));
  const acceptAll  = () => { const s = {}; CATEGORIES.forEach((c) => (s[c] = "accepted")); setStatuses(s); };
  const rejectAll  = () => { const s = {}; CATEGORIES.forEach((c) => (s[c] = "rejected")); setStatuses(s); };

  const startEdit  = (cat, current) => { setEditingCat(cat); setEditValue(current); };
  const saveEdit   = (cat, value)   => {
    setApiRows((prev) => prev.map((r) => r.category === cat ? { ...r, ai_suggested: value } : r));
    setEditingCat(null);
  };

  // On Back — commit all accepted suggestions to the parent table
  const handleBack = () => {
    const updates = {};
    apiRows.forEach(({ category, ai_suggested }) => {
      if (statuses[category] === "accepted") {
        const fieldKey = FIELD_MAP[category];
        if (fieldKey) updates[fieldKey] = ai_suggested;
      }
    });
    if (Object.keys(updates).length) onSaveRow(row.id, updates);
    onBack();
  };

  const rowBg    = (cat) => statuses[cat] === "accepted" ? "#f0fdfa" : statuses[cat] === "rejected" ? "#fff5f5" : "#fff";
  const rowBorder= (cat) => statuses[cat] === "accepted" ? "#ccfbf1" : statuses[cat] === "rejected" ? "#fecaca" : "#f3f4f6";

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
      <style>{SPIN}</style>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px 16px", borderBottom: "1px solid #f3f4f6" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={handleBack}
            style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", color: "#6b7280", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#111827")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
          >
            <ChevLeft /> Back
          </button>
          <div>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#111827", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "#0d9488" }}><StarIcon size={16} /></span>
              AI Suggestions Comparison
            </h2>
            <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px", marginBottom: 0 }}>
              Review AI-generated improvements for metadata fields
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={rejectAll}
            style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: 500, background: "#fff", color: "#ef4444", border: "1px solid #fca5a5", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff5f5")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            <XIcon size={12} /> Reject All
          </button>
          <button
            onClick={acceptAll}
            style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: 500, background: "#0d9488", color: "#fff", border: "none", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#0f766e")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#0d9488")}
          >
            <CheckIcon size={12} /> Accept All
          </button>
        </div>
      </div>

      {/* ── Field info bar ── */}
      <div style={{ padding: "12px 24px", background: "#f9fefe", borderBottom: "1px solid #e8f5f3", display: "flex", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: "13px", color: "#6b7280", marginRight: "4px" }}>Field Name:</span>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827", marginRight: "16px" }}>{row.fieldName}</span>
        <span style={{ color: "#e5e7eb", marginRight: "16px" }}>|</span>
        <span style={{ fontSize: "13px", color: "#6b7280", marginRight: "6px" }}>Type:</span>
        <span style={{ marginRight: "16px" }}><TypeBadge type={row.type} /></span>
        <span style={{ color: "#e5e7eb", marginRight: "16px" }}>|</span>
        <span style={{ fontSize: "13px", color: "#6b7280", marginRight: "4px" }}>CDK:</span>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827", marginRight: "16px" }}>{row.cdk}</span>
        <span style={{ color: "#e5e7eb", marginRight: "16px" }}>|</span>
        <span style={{ fontSize: "13px", color: "#6b7280", marginRight: "4px" }}>Sample Value:</span>
        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{row.sampleValue}</span>
      </div>

      {/* ── Comparison table ── */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <thead>
          <tr style={{ background: "#fafafa", borderBottom: "1px solid #f3f4f6" }}>
            <th style={{ textAlign: "left", padding: "10px 20px", fontWeight: 500, color: "#6b7280", fontSize: "12px", width: "180px" }}>Category</th>
            <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 500, color: "#6b7280", fontSize: "12px", width: "35%" }}>Previous</th>
            <th style={{ textAlign: "left", padding: "10px 16px", fontWeight: 500, fontSize: "12px", width: "35%" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#0d9488" }}>
                <StarIcon size={12} /> AI Suggested
              </span>
            </th>
            <th style={{ textAlign: "right", padding: "10px 20px", fontWeight: 500, color: "#6b7280", fontSize: "12px", width: "240px" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={4} className="py-8 text-center">
                <div className="flex items-center justify-center h-[300px]">
                  <div className="flex flex-col items-center space-y-3">
                    <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
                    <span className="text-gray-700 text-[20px] text-center">Generating AI suggestions...</span>
                  </div>
                </div>
              </td>
            </tr>
          ) : hasError ? (
            <tr>
              <td colSpan={4} style={{ padding: "48px 20px" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <p style={{ fontSize: "14px", fontWeight: 600, color: "#374151", margin: 0 }}>Something went wrong</p>
                  <p style={{ fontSize: "13px", color: "#9ca3af", margin: 0 }}>Unable to load AI suggestions. Please try again later.</p>
                </div>
              </td>
            </tr>
          ) : (
            apiRows.map((item, idx) => {
              const { category, previous, ai_suggested } = item;
              const status    = statuses[category];
              const isEditing = editingCat === category;
              const isLast    = idx === apiRows.length - 1;

              return (
                <tr
                  key={category}
                  style={{ background: rowBg(category), borderBottom: isLast ? "none" : `1px solid ${rowBorder(category)}`, transition: "background 0.15s" }}
                >
                  {/* Category */}
                  <td style={{ padding: "16px 20px", fontWeight: 600, color: "#1f2937", verticalAlign: "top" }}>
                    {category}
                  </td>

                  {/* Previous */}
                  <td style={{ padding: "16px 16px", verticalAlign: "top" }}>
                    <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", color: !previous || previous === "N/A" ? "#d1d5db" : "#6b7280", fontSize: "13px", minHeight: "36px" }}>
                      {previous || "N/A"}
                    </div>
                  </td>

                  {/* AI Suggested */}
                  <td style={{ padding: "16px 16px", verticalAlign: "top" }}>
                    {isEditing ? (
                      <EditableCell
                        value={editValue}
                        onChange={setEditValue}
                        onSave={() => saveEdit(category, editValue)}
                        onCancel={() => setEditingCat(null)}
                      />
                    ) : (
                      <SuggestionCell
                        suggested={ai_suggested}
                        status={status}
                        onEdit={() => startEdit(category, ai_suggested)}
                      />
                    )}
                  </td>

                  {/* Actions */}
                  <td style={{ padding: "16px 20px", verticalAlign: "top" }}>
                    <ActionButtons
                      status={status}
                      disabled={isEditing}
                      onAccept={() => setStatus(category, "accepted")}
                      onReject={() => setStatus(category, "rejected")}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}